import { useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Upload, FileSpreadsheet, CheckCircle, AlertCircle, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BankingImport() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/banking/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      toast.success(`Imported ${response.data.imported} transactions`);
      setFile(null);
      // Reset file input
      document.getElementById('bank-file-input').value = '';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import file'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banking-import">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Import Bank Statement</h1>
          <p className="text-muted-foreground mt-1">Upload your bank statement CSV to track all transactions</p>
        </div>

        {/* Instructions */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>CSV Format Requirements</AlertTitle>
          <AlertDescription>
            Your CSV file should have columns for: <strong>Date</strong>, <strong>Description</strong> (or Narrative/Details), 
            and either <strong>Amount</strong> (positive for credits, negative for debits) or separate <strong>Debit</strong> and <strong>Credit</strong> columns.
          </AlertDescription>
        </Alert>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Statement
            </CardTitle>
            <CardDescription>Select a CSV file from your bank to import transactions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <div className="space-y-2">
                <Label htmlFor="bank-file-input" className="cursor-pointer">
                  <span className="text-primary hover:underline">Click to select</span> or drag and drop
                </Label>
                <Input
                  id="bank-file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <p className="text-sm text-muted-foreground">CSV files only</p>
              </div>
            </div>

            {file && (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button onClick={handleUpload} disabled={uploading}>
                  {uploading ? 'Importing...' : 'Import'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Import Result */}
        {result && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                Import Successful
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-muted-foreground">Transactions Imported</p>
                  <p className="text-3xl font-bold text-green-600">{result.imported}</p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-muted-foreground">Uncategorized</p>
                  <p className="text-3xl font-bold text-amber-600">{result.uncategorized}</p>
                  {result.uncategorized > 0 && (
                    <Button variant="link" className="p-0 h-auto text-sm" asChild>
                      <a href="/admin/banking/uncategorized">Review now →</a>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Tips for Best Results</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 text-green-600" />
                Download your statement in CSV format from your bank's online portal
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 text-green-600" />
                Set up category rules first to auto-categorize common transactions
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 text-green-600" />
                Import statements monthly to keep your ledger up to date
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600" />
                Review uncategorized transactions after each import
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
